import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../utils/api';

const InvestorCompanyView = () => {
  const { companyName } = useParams();
  const navigate = useNavigate();
  const [portalData, setPortalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedSections, setExpandedSections] = useState({});
  const [activeTab, setActiveTab] = useState('documents');
  const [ndaSigned, setNdaSigned] = useState(false);
  const [showNDAModal, setShowNDAModal] = useState(false);
  const [ndaLoading, setNdaLoading] = useState(false);
  const [bioPopupMember, setBioPopupMember] = useState(null);
  const [displayName, setDisplayName] = useState('');

  useEffect(() => {
    fetchPortalData();
    // Fetch the same companies list the dashboard uses so the title matches exactly.
    (async () => {
      try {
        const r = await api.get(`${process.env.REACT_APP_BACKEND_API_URL}/investor-accounts/companies`);
        if (r.ok) {
          const d = await r.json();
          const match = (d.companies || []).find(c => c.name === decodeURIComponent(companyName));
          if (match) setDisplayName(match.officialName || match.name);
        }
      } catch (e) { console.error('Error fetching companies for title:', e); }
    })();
  }, [companyName]);

  const fetchPortalData = async () => {
    try {
      const response = await api.get(
        `${process.env.REACT_APP_BACKEND_API_URL}/investor-accounts/company/${encodeURIComponent(companyName)}/portal`
      );
      const data = await response.json();

      if (response.ok) {
        setPortalData(data);
        setNdaSigned(data.ndaSigned || false);
      } else {
        setError(data.error || 'Failed to load portal data');
      }
    } catch (err) {
      setError('Failed to load portal data');
    } finally {
      setLoading(false);
    }
  };

  const handleSignNDA = async () => {
    try {
      setNdaLoading(true);
      const response = await api.post(
        `${process.env.REACT_APP_BACKEND_API_URL}/investor-accounts/company/${encodeURIComponent(companyName)}/sign-nda`
      );

      if (response.ok) {
        setShowNDAModal(false);
        setNdaSigned(true);
        // Refresh portal data to get confidential files
        await fetchPortalData();
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to sign NDA');
      }
    } catch (err) {
      alert('Failed to sign NDA. Please try again.');
    } finally {
      setNdaLoading(false);
    }
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    const num = parseInt(bytes);
    if (num < 1024) return `${num} B`;
    if (num < 1024 * 1024) return `${(num / 1024).toFixed(1)} KB`;
    return `${(num / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading portal data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/investor-dashboard')}
          className="text-indigo-600 hover:text-indigo-500 font-medium text-sm mb-4 flex items-center"
        >
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Dashboard
        </button>
        <h1 className="text-3xl font-bold text-gray-900">{displayName || portalData?.officialName || decodeURIComponent(companyName)}</h1>
        <p className="text-gray-600 mt-2">Investor Portal — Documents, team information, and investor materials</p>
        {portalData?.logoUrl && (
          <div className="mt-4">
            <img src={portalData.logoUrl} alt="Company logo" className="max-h-24 object-contain" />
          </div>
        )}
        {portalData?.overview && (
          <div className="mt-4 bg-white rounded-lg border border-gray-200 p-4">
            <h2 className="text-sm font-semibold text-gray-700 mb-1">Company Overview</h2>
            <p className="text-sm text-gray-800 whitespace-pre-wrap">{portalData.overview}</p>
          </div>
        )}
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {portalData && (
        <>
          {/* Tabs */}
          <div className="border-b border-gray-200 mb-6">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('documents')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'documents'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Documents
              </button>
              <button
                onClick={() => setActiveTab('team')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'team'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Team
              </button>
              <button
                onClick={() => {
                  if (!portalData?.hasIP || ndaSigned) {
                    setActiveTab('ip');
                  } else {
                    setShowNDAModal(true);
                  }
                }}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'ip'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } ${(!ndaSigned && portalData?.hasIP) ? 'opacity-70' : ''}`}
                title={(ndaSigned || !portalData?.hasIP) ? '' : 'Sign NDA to view'}
              >
                Intellectual Property {(!ndaSigned && portalData?.hasIP) && '🔒'}
              </button>
              <button
                onClick={() => {
                  if (!portalData?.hasFunding || ndaSigned) {
                    setActiveTab('funding');
                  } else {
                    setShowNDAModal(true);
                  }
                }}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'funding'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } ${(!ndaSigned && portalData?.hasFunding) ? 'opacity-70' : ''}`}
                title={(ndaSigned || !portalData?.hasFunding) ? '' : 'Sign NDA to view'}
              >
                Funding {(!ndaSigned && portalData?.hasFunding) && '🔒'}
              </button>
            </nav>
          </div>

          {/* Documents Tab - Collapsible Sections */}
          {activeTab === 'documents' && (
            <div className="space-y-3">
              {/* NDA Signed Indicator */}
              {ndaSigned && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-green-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-green-900">NDA Signed</p>
                      <p className="text-xs text-green-700 mt-0.5">You have signed an NDA for this company. You can access all confidential documents.</p>
                    </div>
                  </div>
                </div>
              )}

              {/* NDA Sign Button - only when confidential files actually exist */}
              {!ndaSigned && (portalData.confidentialFileTotal > 0) && (
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <svg className="w-5 h-5 text-indigo-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                      <div>
                        <p className="text-sm font-medium text-indigo-900">Confidential documents are available</p>
                        <p className="text-xs text-indigo-700 mt-0.5">Sign an NDA to access confidential materials shared by this company.</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowNDAModal(true)}
                      className="ml-4 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 transition-colors flex items-center whitespace-nowrap"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                      Sign NDA for Access to Confidential Documents
                    </button>
                  </div>
                </div>
              )}

              {portalData.sections && portalData.sections.map(section => {
                const isExpanded = expandedSections[section.id];
                const totalItems = section.fileCount;

                // Only show sections that have files
                if (totalItems === 0 && !(section.confidentialCount > 0)) return null;

                return (
                  <div key={section.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                    {/* Section Header */}
                    <div
                      className="flex justify-between items-center p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                      onClick={() => toggleSection(section.id)}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-gray-400 text-sm">{isExpanded ? '▼' : '▶'}</span>
                        <h3 className="text-lg font-semibold text-gray-900">{section.name}</h3>
                        <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">
                          {totalItems} file{totalItems !== 1 ? 's' : ''}
                        </span>
                        {section.confidentialCount > 0 && (
                          <span className="px-2 py-0.5 text-xs bg-amber-100 text-amber-700 rounded-full font-medium">
                            {section.confidentialCount} confidential
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Expanded Section Content */}
                    {isExpanded && (
                      <div className="border-t border-gray-200">
                        {(section.files && section.files.length > 0) ? (
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">File Name</th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Size</th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Uploaded</th>
                                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {section.files && section.files.map(file => (
                                  <tr key={file.id || file.originalName} className="hover:bg-gray-50">
                                    <td className="px-4 py-3 text-sm text-gray-900">
                                      <div className="flex items-center">
                                        <svg className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                        <div className="flex items-center gap-2">
                                          <span>{file.originalName}</span>
                                          {file.confidential && (
                                            <span className="px-1.5 py-0.5 text-xs bg-amber-100 text-amber-700 rounded font-medium">Confidential</span>
                                          )}
                                          {file.locked && (
                                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                            </svg>
                                          )}
                                        </div>
                                        {file.description && (
                                          <p className="text-xs text-gray-500 mt-0.5 ml-6">{file.description}</p>
                                        )}
                                      </div>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-500">
                                      {file.locked ? '' : formatFileSize(file.size)}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-500">
                                      {file.locked ? '' : (file.createdAt ? new Date(file.createdAt).toLocaleDateString() : 'N/A')}
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                      {file.locked ? (
                                        <span className="text-xs text-gray-400">Sign NDA to access</span>
                                      ) : (
                                        <div className="flex items-center justify-center gap-3">
                                          <a
                                            href={`${process.env.REACT_APP_BACKEND_API_URL}/investor-accounts/company/${encodeURIComponent(companyName)}/files/${file.id}/view`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-indigo-600 hover:text-indigo-700 text-sm font-medium"
                                          >
                                            View
                                          </a>
                                          <a
                                            href={`${process.env.REACT_APP_BACKEND_API_URL}/investor-accounts/company/${encodeURIComponent(companyName)}/files/${file.id}/download`}
                                            className="text-indigo-600 hover:text-indigo-700 text-sm font-medium"
                                          >
                                            Download
                                          </a>
                                        </div>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <div className="px-4 py-6 text-center">
                            <p className="text-sm text-gray-500">No files in this section</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Show message if no sections have files */}
              {portalData.sections && portalData.sections.every(s => s.fileCount === 0) && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
                  <p className="text-gray-500">No documents are available for viewing at this time.</p>
                </div>
              )}
            </div>
          )}

          {/* Team Tab */}
          {activeTab === 'team' && (
            <div>
              {portalData.teamMembers && portalData.teamMembers.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {portalData.teamMembers.map((member) => {
                    if (!member) return null;
                    return (
                    <div key={member.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                      {member.headshotUrl ? (
                        <img
                          src={member.headshotUrl}
                          alt={member.name || 'Team member'}
                          className="w-24 h-24 rounded-full mx-auto mb-4 object-cover"
                        />
                      ) : (
                        <div className="w-24 h-24 bg-indigo-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                          <span className="text-indigo-600 font-bold text-2xl">
                            {member.name ? member.name.charAt(0).toUpperCase() : '?'}
                          </span>
                        </div>
                      )}
                      <h3 className="text-lg font-semibold text-gray-900 text-center">{member.name || 'Unknown'}</h3>
                      <p className="text-sm text-gray-500 text-center mb-2">{member.title || ''}</p>
                      {member.bio && (
                        <button
                          onClick={() => setBioPopupMember(member)}
                          className="text-sm text-indigo-600 hover:text-indigo-700 hover:underline cursor-pointer mt-1"
                        >
                          View Bio
                        </button>
                      )}
                    </div>
                    );
                  })}
                </div>
              ) : (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
                  <p className="text-gray-500">No team information available at this time.</p>
                </div>
              )}
            </div>
          )}

          {/* Intellectual Property Tab */}
          {activeTab === 'ip' && (
            !portalData.hasIP ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
                <p className="text-gray-500">No intellectual property records have been added yet.</p>
              </div>
            ) : !ndaSigned ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
                <p className="text-gray-700 font-medium">🔒 Sign the NDA to view this company's intellectual property.</p>
                <button onClick={() => setShowNDAModal(true)} className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700">Sign NDA</button>
              </div>
            ) : !portalData.patents || portalData.patents.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
                <p className="text-gray-500">No intellectual property records have been added yet.</p>
              </div>
            ) : (
              <div className="space-y-6">
                {portalData.patents.map((p) => {
                  const rows = [
                    {
                      country: p.country, appType: p.appType, patentStatus: p.patentStatus,
                      serialNo: p.serialNo, fileDate: p.fileDate, patentNo: p.patentNo, issueDate: p.issueDate
                    },
                    ...(Array.isArray(p.filings) ? p.filings : [])
                  ];
                  return (
                  <div key={p.id} className="bg-white border rounded-lg overflow-hidden">
                    <div className="bg-gray-50 px-4 py-2 border-b">
                      <h3 className="font-semibold text-gray-900">{p.title}</h3>
                    </div>
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-3 py-2 text-left">Country</th>
                          <th className="px-3 py-2 text-left">App Type</th>
                          <th className="px-3 py-2 text-left">Patent Status</th>
                          <th className="px-3 py-2 text-left">Serial No</th>
                          <th className="px-3 py-2 text-left">File Date</th>
                          <th className="px-3 py-2 text-left">Patent No</th>
                          <th className="px-3 py-2 text-left">Issue Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((row, idx) => (
                          <tr key={idx} className={idx > 0 ? 'border-t' : ''}>
                            <td className="px-3 py-2">{row.country || '—'}</td>
                            <td className="px-3 py-2">{row.appType || '—'}</td>
                            <td className="px-3 py-2">{row.patentStatus || '—'}</td>
                            <td className="px-3 py-2">{row.serialNo || '—'}</td>
                            <td className="px-3 py-2">{row.fileDate || '—'}</td>
                            <td className="px-3 py-2">{row.patentNo || '—'}</td>
                            <td className="px-3 py-2">{row.issueDate || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  );
                })}
              </div>
            )
          )}

          {/* Funding Tab */}
          {activeTab === 'funding' && (
            !portalData.hasFunding ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
                <p className="text-gray-500">No funding entries have been added yet.</p>
              </div>
            ) : !ndaSigned ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
                <p className="text-gray-700 font-medium">🔒 Sign the NDA to view this company's funding history.</p>
                <button onClick={() => setShowNDAModal(true)} className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700">Sign NDA</button>
              </div>
            ) : !portalData.funding || portalData.funding.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
                <p className="text-gray-500">No funding entries have been added yet.</p>
              </div>
            ) : (
              <div className="bg-white border rounded-lg overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-3 py-2 text-left">Date Awarded</th>
                      <th className="px-3 py-2 text-left">Total Amount</th>
                      <th className="px-3 py-2 text-left">Granting Agency</th>
                      <th className="px-3 py-2 text-left">Grant Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {portalData.funding.map((f) => (
                      <tr key={f.id} className="border-t">
                        <td className="px-3 py-2">{f.dateAwarded || '—'}</td>
                        <td className="px-3 py-2">{f.totalAmountAwarded || '—'}</td>
                        <td className="px-3 py-2">{f.grantingAgency || '—'}</td>
                        <td className="px-3 py-2">{f.grantType === 'Other' ? `Other: ${f.grantTypeOther || ''}` : (f.grantType || '—')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}
        </>
      )}

      {/* Bio Popup Modal */}
      {bioPopupMember && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">{bioPopupMember.name}</h2>
                {bioPopupMember.title && (
                  <p className="text-sm text-gray-600 mt-1">{bioPopupMember.title}</p>
                )}
              </div>
              <button
                onClick={() => setBioPopupMember(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{bioPopupMember.bio}</p>
          </div>
        </div>
      )}

      {/* NDA Modal */}
      {showNDAModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Non-Disclosure Agreement</h2>
            <div className="mb-6">
              <p className="text-sm text-gray-700 leading-relaxed">
                {/* By signing this NDA, you agree to keep all confidential information shared through this portal
                strictly confidential. You agree not to disclose, share, or use the information for any purpose
                other than evaluating potential investment opportunities. */}
                Temp
              </p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleSignNDA}
                disabled={ndaLoading}
                className={`flex-1 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 transition-colors ${
                  ndaLoading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {ndaLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Signing...
                  </div>
                ) : (
                  'I Agree'
                )}
              </button>
              <button
                onClick={() => setShowNDAModal(false)}
                disabled={ndaLoading}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvestorCompanyView;
